import { SplitPayBadges } from "@/components/PayIn4";
import React from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Check, ArrowLeft, ShoppingBag, Lock, Truck, MessageCircle, Info } from "lucide-react";
import { getPack, fmt, PRODUCTS, PACK_PREVIEW, PACK_COMPOSITION } from "@/data/packs";
import { useCart } from "@/context/CartContext";
import { ZoomImage } from "@/components/ZoomImage";
import { useSeo, useJsonLd, SITE_URL } from "@/lib/seo";

const Block = ({ title, children }) => (
  <div>
    <h3 className="font-serif text-xl text-[var(--oz-text)] mb-3">{title}</h3>
    <div className="text-sm text-[var(--oz-muted)] leading-relaxed">{children}</div>
  </div>
);

export default function ProductPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addItem, setOpen } = useCart();
  const pack = getPack(slug);

  useSeo({
    title: pack ? `${pack.name} — ${pack.products} soins Ozentya à revendre` : "Pack — Ozentya",
    description: pack ? pack.description : "",
    path: `/pack/${slug}`,
    image: pack ? pack.image : undefined,
  });

  useJsonLd(
    "ld-pack",
    pack
      ? {
          "@context": "https://schema.org",
          "@type": "Product",
          name: pack.name,
          image: pack.image,
          description: pack.description,
          brand: { "@type": "Brand", name: "Ozentya" },
          offers: {
            "@type": "Offer",
            price: String(pack.price),
            priceCurrency: "EUR",
            availability: "https://schema.org/InStock",
            url: `${SITE_URL}/pack/${pack.slug}`,
          },
        }
      : null
  );

  if (!pack) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center">
        <h1 className="font-serif text-3xl mb-4">Pack introuvable</h1>
        <Link to="/" className="text-[var(--oz-gold)] font-semibold">Retour à l'accueil</Link>
      </div>
    );
  }

  const buyNow = () => {
    addItem(pack.id);
    setOpen(true);
  };

  return (
    <div className="px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto pt-10 md:pt-14 pb-28 lg:pb-14">
      <button
        data-testid="product-back"
        onClick={() => navigate("/packs")}
        className="inline-flex items-center gap-2 py-2 text-sm text-[var(--oz-muted)] hover:text-[var(--oz-text)] mb-6"
      >
        <ArrowLeft className="w-4 h-4" /> Retour aux packs
      </button>

      <div className="grid lg:grid-cols-2 gap-10 lg:gap-16">
        <div className="lg:sticky lg:top-24 h-fit">
          <div className="relative rounded-[1.75rem] overflow-hidden shadow-[0_30px_60px_-30px_rgba(26,23,20,0.5)]">
            <img src={pack.image} alt={pack.name} className="w-full h-[340px] sm:h-[460px] object-cover" />
            {pack.badge && (
              <div className="absolute top-4 left-4 bg-[var(--oz-dark)] text-[var(--oz-gold)] text-[11px] font-bold uppercase tracking-[0.14em] px-3 py-1.5 rounded-full">
                {pack.badge}
              </div>
            )}
          </div>
          <div className="mt-4 flex flex-wrap gap-4 text-xs text-[var(--oz-muted)]">
            <span className="inline-flex items-center gap-1.5"><Lock className="w-4 h-4 text-[var(--oz-gold)]" /> Paiement sécurisé</span>
            <span className="inline-flex items-center gap-1.5"><Truck className="w-4 h-4 text-[var(--oz-gold)]" /> Livraison suivie offerte</span>
            <span className="inline-flex items-center gap-1.5"><MessageCircle className="w-4 h-4 text-[var(--oz-gold)]" /> Support client</span>
          </div>
        </div>

        <div>
          <span className="inline-block text-xs font-semibold uppercase tracking-[0.2em] text-[var(--oz-gold)] mb-3">
            {pack.tagline}
          </span>
          <h1 className="font-serif text-4xl sm:text-5xl font-medium tracking-tight text-[var(--oz-text)]">
            {pack.name}
          </h1>
          <div className="mt-4 flex items-baseline gap-2">
            <span className="font-serif text-4xl text-[var(--oz-text)]">{fmt(pack.price)}</span>
            <span className="text-sm text-[var(--oz-muted)]">• {pack.products} produits</span>
          </div>
          <SplitPayBadges amount={pack.price} className="mt-3" testid="product-pay4" />

          <p className="mt-5 text-base text-[var(--oz-muted)] leading-relaxed">{pack.description}</p>

          <div className="mt-8">
            <h3 className="font-serif text-xl text-[var(--oz-text)] mb-4">Aperçu des produits inclus</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              {PACK_PREVIEW.map((key) => {
                const prod = PRODUCTS[key];
                const comp = (PACK_COMPOSITION[pack.id] || []).find((c) => c.key === key);
                return (
                  <Link
                    key={key}
                    to={`/produit/${key}`}
                    data-testid={`product-preview-${key}`}
                    className="group bg-white rounded-2xl border border-[var(--oz-border)] overflow-hidden hover:-translate-y-1 hover:shadow-lg transition-all block"
                  >
                    <div className="relative aspect-square bg-[var(--oz-bg-2)]">
                      <ZoomImage src={prod.image} alt={prod.name} zoom={2} className="w-full h-full" />
                      {comp && (
                        <span className="absolute top-2 right-2 z-10 bg-[var(--oz-dark)] text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                          x{comp.qty}
                        </span>
                      )}
                    </div>
                    <div className="p-2.5 text-center">
                      <span className="text-[11px] font-semibold text-[var(--oz-text)] leading-tight group-hover:text-[var(--oz-gold)] transition-colors">{prod.name}</span>
                    </div>
                  </Link>
                );
              })}
            </div>
            <p className="mt-3 text-xs text-[var(--oz-muted)] italic">
              Aperçu de la gamme Ozentya incluse dans le pack. La composition exacte peut varier selon les disponibilités.
            </p>
          </div>

          <div className="mt-8 space-y-7">
            <Block title="Ce que contient le pack">
              <ul className="space-y-2.5">
                {pack.contains.map((c, i) => (
                  <li key={i} className="flex items-start gap-2.5">
                    <span className="mt-0.5 flex-shrink-0 w-5 h-5 rounded-full bg-[var(--oz-nude)] text-[var(--oz-badge)] flex items-center justify-center">
                      <Check className="w-3 h-3" />
                    </span>
                    <span>{c}</span>
                  </li>
                ))}
              </ul>
            </Block>

            <Block title="À qui s'adresse-t-il ?">{pack.audience}</Block>

            <Block title="Comment démarrer ?">
              Choisissez votre pack, ajoutez-le au panier et validez votre commande. Vous recevrez
              votre sélection de produits Ozentya, prête à être présentée et proposée à votre
              entourage et à vos clients.
            </Block>

            <div className="bg-[var(--oz-nude)]/50 border border-[var(--oz-border)] rounded-2xl p-5">
              <div className="flex items-center gap-2 mb-2">
                <Info className="w-4 h-4 text-[var(--oz-badge)]" />
                <span className="font-semibold text-sm text-[var(--oz-text)]">Informations légales</span>
              </div>
              <p className="text-xs text-[var(--oz-muted)] leading-relaxed">
                Aucun revenu n'est garanti. La revente occasionnelle peut être possible en tant que
                particulier ; une activité habituelle et lucrative peut nécessiter un statut adapté.
                Le revendeur agit en son nom et sous sa propre responsabilité.
              </p>
            </div>
          </div>

          <div className="mt-8 hidden sm:flex flex-col sm:flex-row gap-3">
            <button
              data-testid="product-add-to-cart"
              onClick={() => addItem(pack.id)}
              className="flex-1 inline-flex items-center justify-center gap-2 border-2 border-[var(--oz-dark)] px-6 py-4 rounded-full font-semibold text-sm tracking-wide hover:bg-[var(--oz-dark)] hover:text-white transition-colors"
            >
              <ShoppingBag className="w-4 h-4" /> AJOUTER AU PANIER
            </button>
            <button
              data-testid="product-buy-now"
              onClick={buyNow}
              className="flex-1 inline-flex items-center justify-center px-6 py-4 rounded-full bg-[var(--oz-gold)] text-white font-semibold text-sm tracking-wide hover:bg-[var(--oz-gold-hover)] transition-colors"
            >
              ACHETER MAINTENANT
            </button>
          </div>
        </div>
      </div>

      {/* Barre d'achat fixe — mobile uniquement */}
      <div
        data-testid="mobile-buy-bar"
        className="sm:hidden fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-md border-t border-[var(--oz-border)] px-4 py-3 flex items-center gap-3"
        style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}
      >
        <div className="flex flex-col leading-tight">
          <span className="font-serif text-xl text-[var(--oz-text)]">{fmt(pack.price)}</span>
          <span className="text-[11px] text-[var(--oz-muted)]">{pack.products} produits • Livraison offerte</span>
        </div>
        <button
          data-testid="mobile-buy-bar-cta"
          onClick={buyNow}
          className="ml-auto flex-1 max-w-[60%] inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full bg-[var(--oz-gold)] text-white font-semibold text-sm tracking-wide active:scale-[0.98] transition-transform"
        >
          <ShoppingBag className="w-4 h-4" /> AJOUTER
        </button>
      </div>
    </div>
  );
}
